'use client';

import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { Service } from '@/core/domain';
import type { CreateServiceInput } from '../../domain/usecases/create-service';
import type { GetUserUidUseCase } from '@/features/authentication/domain/usecases/get-user-uid';
import type { GetAllServicesUseCase } from '../../domain/usecases/get-all-services';
import type { CreateServiceUseCase } from '../../domain/usecases/create-service';
import type { UpdateServiceUseCase } from '../../domain/usecases/update-service';
import type { DeleteServiceUseCase } from '../../domain/usecases/delete-service';
import { ReminderContext } from '@/features/app/reminders/presentation/context/ReminderContext';

export interface ServiceState {
  services: Service[];
  loading: boolean;
  error: string | null;
}

/** Resultado de operações; usado para feedback (toast). Após sucesso, o refresh atualiza o estado. */
export interface ServiceOperationResult {
  success: boolean;
  error: string | null;
}

export interface ServiceEvents {
  /** Refresh: carrega lista de serviços do usuário. `forceRefresh` ignora cache local primeiro. */
  getByUserId: (options?: { forceRefresh?: boolean }) => Promise<Service[]>;
  create: (input: CreateServiceInput) => Promise<ServiceOperationResult>;
  update: (id: string, data: Partial<Service>) => Promise<ServiceOperationResult>;
  delete: (id: string) => Promise<ServiceOperationResult>;
  clearError: () => void;
}

export interface ServiceContextValue {
  state: ServiceState;
  events: ServiceEvents;
}

export interface ServiceProviderProps {
  children: React.ReactNode;
  getUserUidUseCase: GetUserUidUseCase;
  getAllServicesUseCase: GetAllServicesUseCase;
  createServiceUseCase: CreateServiceUseCase;
  updateServiceUseCase: UpdateServiceUseCase;
  deleteServiceUseCase: DeleteServiceUseCase;
}

const initialState: ServiceState = {
  services: [],
  loading: false,
  error: null,
};

const ServiceContext = createContext<ServiceContextValue | null>(null);

export function ServiceProvider({
  children,
  getUserUidUseCase,
  getAllServicesUseCase,
  createServiceUseCase,
  updateServiceUseCase,
  deleteServiceUseCase,
}: ServiceProviderProps) {
  const [state, setState] = useState<ServiceState>(initialState);
  const remindersCtx = useContext(ReminderContext);

  const refreshRemindersIfAvailable = useCallback(() => {
    void remindersCtx?.events.getByUserId({ forceRefresh: true });
  }, [remindersCtx]);

  const clearError = useCallback(() => {
    setState((s) => ({ ...s, error: null }));
  }, []);

  const getUserUid = useCallback(async (): Promise<string | null> => {
    const userId = await getUserUidUseCase.execute();
    if (!userId) {
      setState((s) => ({ ...s, error: 'Usuário não autenticado.', services: [] }));
      return null;
    }
    return userId;
  }, [getUserUidUseCase]);

  const getByUserId = useCallback(
    async (options?: { forceRefresh?: boolean }): Promise<Service[]> => {
      const userId = await getUserUid();
      if (!userId) {
        setState((s) => ({ ...s, error: 'Usuário não autenticado.', services: [] }));
        return [];
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        const list = await getAllServicesUseCase.execute(userId, options);
        setState((s) => ({ ...s, services: list }));
        return list;
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage, services: [] }));
        return [];
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, getAllServicesUseCase]
  );

  const create = useCallback(
    async (input: CreateServiceInput): Promise<ServiceOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await createServiceUseCase.execute(userId, input);
        await getByUserId();
        refreshRemindersIfAvailable();
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, createServiceUseCase, getByUserId, refreshRemindersIfAvailable]
  );

  const update = useCallback(
    async (id: string, data: Partial<Service>): Promise<ServiceOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await updateServiceUseCase.execute(userId, id, data);
        await getByUserId();
        refreshRemindersIfAvailable();
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, updateServiceUseCase, getByUserId, refreshRemindersIfAvailable]
  );

  const deleteService = useCallback(
    async (id: string): Promise<ServiceOperationResult> => {
      const userId = await getUserUid();
      if (!userId) {
        const err = 'Usuário não autenticado.';
        setState((s) => ({ ...s, error: err }));
        return { success: false, error: err };
      }
      setState((s) => ({ ...s, loading: true, error: null }));
      try {
        await deleteServiceUseCase.execute(userId, id);
        await getByUserId();
        return { success: true, error: null };
      } catch (e) {
        const errorMessage = e instanceof Error ? e.message : String(e);
        setState((s) => ({ ...s, error: errorMessage }));
        return { success: false, error: errorMessage };
      } finally {
        setState((s) => ({ ...s, loading: false }));
      }
    },
    [getUserUid, deleteServiceUseCase, getByUserId]
  );

  const events: ServiceEvents = useMemo(
    () => ({
      getByUserId,
      create,
      update,
      delete: deleteService,
      clearError,
    }),
    [getByUserId, create, update, deleteService, clearError]
  );

  const value: ServiceContextValue = useMemo(
    () => ({ state, events }),
    [state, events]
  );

  return (
    <ServiceContext.Provider value={value}>
      {children}
    </ServiceContext.Provider>
  );
}

export function useServices(): ServiceContextValue {
  const ctx = useContext(ServiceContext);
  if (!ctx) {
    throw new Error('useServices must be used within ServiceProvider');
  }
  return ctx;
}
